import { useState, useEffect } from 'react';
import { Check, User, Circle, Square, Triangle, Diamond, X } from 'lucide-react';
import { submitAnswer } from '../../services/participantService';
import { getAnswerConfig } from '../../utils/answerConfig';

const LUCIDE_BY_NAME = {
  circle: Circle,
  square: Square,
  triangle: Triangle,
  diamond: Diamond,
  check: Check,
  x: X,
};

export default function AnswerButtons({ sessionId, participantId, question, questionId, hasAnswered, participantName, score, timeLeft, onAnswerResult }) {
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const answerConfig = getAnswerConfig(question.type);
  const isTwoAnswers = question.answers.length <= 2;

  useEffect(() => {
    setSelectedAnswer(null);
    setIsSubmitting(false);
  }, [questionId]);

  const handleAnswerClick = async (answer, index) => {
    if (selectedAnswer || isSubmitting || hasAnswered) return;

    setIsSubmitting(true);
    setSelectedAnswer(index);

    try {
      const result = await submitAnswer(sessionId, participantId, questionId, answer.id);
      onAnswerResult?.({
        isCorrect: result.isCorrect,
        pointsEarned: result.pointsEarned,
      });
    } catch (err) {
      console.error('Error submitting answer:', err);
      onAnswerResult?.({
        isCorrect: false,
        error: err.message,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show waiting state after answering
  if (selectedAnswer !== null || hasAnswered) {
    return (
      <div className="mx-auto flex h-screen max-w-md flex-col bg-mp-darkgreen font-body items-center justify-center text-center p-6">
        <div className="relative mb-8 flex h-32 w-32 items-center justify-center">
          <div className="absolute inset-0 rounded-full bg-mp-lime/30 animate-ping" style={{ animationDuration: '2s' }} />
          <div className="absolute inset-0 rounded-full bg-mp-lime/20 animate-ping" style={{ animationDuration: '2s', animationDelay: '0.5s' }} />
          <div className="relative flex h-24 w-24 items-center justify-center rounded-full bg-mp-lime shadow-mp-3">
            <Check size={48} strokeWidth={3} className="text-mp-darkgreen" />
          </div>
        </div>
        <h2 className="font-display text-3xl text-white mb-2">Svar sendt!</h2>
        <p className="font-body text-white/70 text-lg">Ventar på resultat …</p>
      </div>
    );
  }

  return (
    <div className="mx-auto flex h-screen max-w-md flex-col bg-mp-darkgreen font-body p-4 pt-6">
      <header className="flex-none pb-3">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2.5 bg-white/10 rounded-full pr-3.5 py-1.5 pl-1.5">
            <div className="w-8 h-8 rounded-full bg-mp-bg-lightgreen flex items-center justify-center overflow-hidden">
              <User size={18} className="text-mp-darkgreen" />
            </div>
            <div className="leading-tight">
              <p className="font-display text-[10px] text-mp-lime uppercase tracking-[0.12em]">Spelar</p>
              <p className="font-display text-sm text-white">{participantName || 'Spelar'}</p>
            </div>
          </div>
          <div className="inline-flex flex-col items-end bg-mp-lime text-mp-darkgreen rounded-full px-3.5 py-1.5">
            <span className="font-display text-[9px] uppercase tracking-[0.16em] opacity-70">Poeng</span>
            <span className="font-display text-lg leading-none">{score?.toLocaleString() || 0}</span>
          </div>
        </div>

        {timeLeft !== undefined && (
          <div>
            <div className="flex justify-between font-display text-[12px] text-white/70 mb-1.5">
              <span>Tid att</span>
              <span>{timeLeft}s</span>
            </div>
            <div className="h-3 w-full rounded-full bg-white/10 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-1000 ${timeLeft < 5 ? 'bg-mp-fox-rust' : 'bg-mp-lime'}`}
                style={{ width: `${Math.min(100, (timeLeft / 30) * 100)}%` }}
              />
            </div>
          </div>
        )}
      </header>

      <main className={`flex-1 grid grid-cols-2 gap-3.5 pt-4 pb-4 ${isTwoAnswers ? 'grid-rows-1' : 'grid-rows-2'}`}>
        {question.answers.map((answer, index) => {
          const config = answerConfig[index];
          if (!config) return null;
          const LucideIcon = LUCIDE_BY_NAME[config.lucideName] || Circle;
          return (
            <button
              key={answer.id || index}
              onClick={() => handleAnswerClick(answer, index)}
              disabled={isSubmitting || selectedAnswer !== null}
              className={`relative flex flex-col items-center justify-center rounded-2xl ${config.color} ${config.textColor} shadow-mp-2 active:scale-[0.97] transition-transform disabled:opacity-50 overflow-hidden`}
            >
              <div className="w-16 h-16 rounded-full bg-white/22 flex items-center justify-center mb-2">
                <LucideIcon size={36} strokeWidth={2.5} />
              </div>
              <span className="font-display text-[15px] uppercase tracking-[0.16em] opacity-70">
                {String.fromCharCode(65 + index)}
              </span>
            </button>
          );
        })}
      </main>

      <p className="text-center font-body text-[12px] text-white/50 pb-2">
        Trykk på ditt svar — me sender det vidare til verten.
      </p>
    </div>
  );
}
